import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';

import CardHeader from '@mui/material/CardHeader';
import CardContent from '@mui/material/CardContent';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import { styled } from '@mui/material/styles';
import Paper from '@mui/material/Paper';
import LoadingButton from '@mui/lab/LoadingButton';
import AddCardOutlinedIcon from '@mui/icons-material/AddCardOutlined';
import LinearProgress from '@mui/material/LinearProgress';
import { Box, Alert } from '@mui/material';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Modal from '@mui/material/Modal';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogTitle from '@mui/material/DialogTitle';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import CloseIcon from '@mui/icons-material/Close';
import Snackbar from '@mui/material/Snackbar';
import Chip from '@mui/material/Chip';
import { useStripe, useElements, Elements, CardElement } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import Card from 'react-credit-cards';
import 'react-credit-cards/es/styles-compiled.css';
import {
    FormControl,
    Pagination,
    IconButton,
    Tooltip,
} from "@mui/material";
import { getSubscription, getPaymentIntentData, addPayment, removePayment, resetPaymentNotifications } from './paymentSlice';
import { fetchAddressBilling } from '../AddressBilling/addressBillingSlice';
import { useraccountactivate, resetOnboarding } from '../../../pages/Dashboard/onboardingSlice';

import Notification from '../../../pages/General/Notification';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY);

// Global styling layout dictionary for consistent corporate modal boxes
const enterpriseModalContainerStyles = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: { xs: '92%', sm: 480 },
    backgroundColor: '#FFFFFF',
    borderRadius: '16px',
    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
    border: '1px solid #E2E8F0',
    p: 4,
    outline: 'none',
    boxSizing: 'border-box',
    overflow: 'hidden'
};

const CARD_ELEMENT_OPTIONS = {
    style: {
        base: {
            color: "#1E293B",
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
            fontSmoothing: "antialiased",
            fontSize: "14px",
            fontWeight: "500",
            "::placeholder": {
                color: "#94A3B8",
            },
        },
        invalid: {
            color: "#EF4444",
            iconColor: "#EF4444",
        },
    },
};

const CheckoutForm = ({ client_secret, dispatch, setShowError, setShowMessageType, setShowMessage, setloadingEnable, loading, addressbilling }) => {
    const stripe = useStripe();
    const elements = useElements();
    const [cardHolderName, setCardHolderName] = useState("");

    const handleSubmit = async (event) => {
        event.preventDefault();
        if(cardHolderName.trim() == ""){
            setShowMessage(["Card holder name is required"]);
            setShowMessageType("error");
            setShowError(true);
            return false;
        }

        setloadingEnable(true);

        if (elements == null) {
            return;
        }
        const { setupIntent, error } = await stripe.confirmCardSetup(client_secret, {
            payment_method: {
                card: elements.getElement(CardElement),
                billing_details: {
                    name: cardHolderName,
                    address: {
                        line1: addressbilling && addressbilling.data && addressbilling.data.billing_address,
                        city: addressbilling && addressbilling.data && addressbilling.data.billing_city,
                        state: addressbilling && addressbilling.data && addressbilling.data.billing_state_code,
                        postal_code: addressbilling && addressbilling.data && addressbilling.data.billing_postal_code,
                        country: addressbilling && addressbilling.data && addressbilling.data.billing_country_code,
                    }
                }
            }
        });
        if (error) {
            setShowMessage([error.message]);
            setShowMessageType("error");
            setShowError(true);
        } else {
            setShowMessage(["Card added successfully"]);
            setShowMessageType("success");
            setShowError(true);
            dispatch(addPayment(setupIntent));
        }
        setloadingEnable(false);
    };

    return (
        <Grid container justifyContent="center" spacing={2}>
            <Grid item xs={12}>
                <Typography variant="h6" sx={{ fontWeight: 800, color: '#0F172A', textAlign: 'center', mb: 2, fontSize: '16px' }}>
                    Payment Details
                </Typography>
                <Divider sx={{ borderColor: '#E2E8F0', mb: 3 }} />
                <Box>
                    <form className="stripe_card_sc" onSubmit={handleSubmit}>
                        <Stack spacing={2.5}>
                            <Box sx={{ width: '100%' }}>
                                <Typography variant="body2" sx={{ fontWeight: 600, color: '#475569', mb: 1, fontSize: '12.5px' }}>
                                    Card Holder Name
                                </Typography>
                                <input 
                                    id="card_holder_name" 
                                    onChange={(e) => setCardHolderName(e.target.value)} 
                                    className="StripeElement"
                                    style={{
                                        width: '100%',
                                        padding: '11px 14px',
                                        fontSize: '13.5px',
                                        color: '#1E293B',
                                        fontWeight: 500,
                                        border: '1px solid #E2E8F0',
                                        borderRadius: '10px',
                                        backgroundColor: '#FFFFFF',
                                        boxSizing: 'border-box',
                                        outline: 'none',
                                        transition: 'all 0.15s ease-in-out'
                                    }}
                                />
                            </Box>

                            <Box sx={{ width: '100%' }}>
                                <Typography variant="body2" sx={{ fontWeight: 600, color: '#475569', mb: 1, fontSize: '12.5px' }}>
                                    Card Details
                                </Typography>
                                <Box sx={{ 
                                    border: '1px solid #E2E8F0', 
                                    borderRadius: '10px', 
                                    p: 1.5,
                                    backgroundColor: '#FFFFFF'
                                }}>
                                    <CardElement options={CARD_ELEMENT_OPTIONS} />
                                </Box>
                            </Box>

                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', pt: 1.5, gap: 1.5 }}>
                                <Button 
                                    variant="outlined" 
                                    size="small"
                                    onClick={() => elements && elements.getElement(CardElement).clear()}
                                    sx={{
                                        textTransform: 'none',
                                        borderRadius: '10px',
                                        fontWeight: 700,
                                        fontSize: '13px',
                                        height: '36px',
                                        borderColor: '#E2E8F0',
                                        color: '#475569',
                                        px: 2.5,
                                        '&:hover': { borderColor: '#CBD5E1', backgroundColor: '#F8FAFC' }
                                    }}
                                >
                                    Clear
                                </Button>
                                <LoadingButton
                                    variant="contained"
                                    type="submit"
                                    disabled={!stripe || !elements}
                                    loading={loading}
                                    sx={{
                                        textTransform: 'none',
                                        borderRadius: '10px',
                                        fontWeight: 700,
                                        fontSize: '13px',
                                        height: '36px',
                                        px: 3.5,
                                        boxShadow: 'none',
                                        backgroundColor: '#1976d2',
                                        color: '#FFFFFF',
                                        '&:hover': { backgroundColor: '#115293', boxShadow: 'none' },
                                        '&.Mui-disabled': { backgroundColor: '#E2E8F0', color: '#94A3B8' }
                                    }}
                                >
                                    Submit
                                </LoadingButton>
                            </Box>
                        </Stack>
                    </form>
                </Box>
            </Grid>
        </Grid>
    );
};

const Item = styled(Paper)(({ theme }) => ({
    backgroundColor: theme.palette.mode === 'dark' ? '#1A2027' : '#fff',
    ...theme.typography.body2,
    padding: theme.spacing(1),
    textAlign: 'center',
    color: theme.palette.text.secondary,
}));

const PaymentPage = () => {
    const dispatch = useDispatch();
    const profile = useSelector(state => state.auth.profile);    
    const activation = useSelector(state => state.onboarding && state.onboarding.activation);  
    const addPaymentData = useSelector(state => state.payment.addPayment);
    const paymentIntentData = useSelector(state => state.payment.paymentIntent);
    const removePaymentData = useSelector(state => state.payment.removePayment);
    const activePaymentData = useSelector(state => state.payment.getActivePayment);
    const addressbilling = useSelector(state => state.addressbilling);
    const [loadingEnable, setloadingEnable] = useState(false);
    const [queryCheckout, setQueryCheckout] = useState("");
    const [alertOpen, setAlertOpen] = useState(false);
    const [isPoll, setIsPoll] = useState(true);
    const [pollingId, setPollingId] = useState(0);
    const [showError, setShowError] = useState(false);
    const [showMessageType, setShowMessageType] = useState("error");
    const [showMessage, setShowMessage] = useState([]);
    const [showCardModal, setShowCardModal] = useState(false);
   
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        let checkout = params.get('checkout');
        if (checkout && checkout !== "undefined") {
            setQueryCheckout(checkout);
        }
    }, []);

    useEffect(() => {
        dispatch(fetchAddressBilling());
        dispatch(getSubscription(queryCheckout));
    }, [queryCheckout]);

    const polling = () => {
        const intervalID = setInterval(() => {
            setIsPoll(false);
            dispatch(getSubscription("is_removed"));
        }, 3000);
        setPollingId(intervalID);
    };

    useEffect(() => {
        if (activePaymentData.data) {
            if (activePaymentData.data.reload && activePaymentData.data.reload === true) {
                setloadingEnable(true);
                if (isPoll === true) {
                    polling();
                }
                setTimeout(function () {
                    setloadingEnable(false);
                    clearInterval(pollingId);
                }, 10000);
            }
            if (activePaymentData.data.reload === false) {
                setloadingEnable(false);
                clearInterval(pollingId);
            }
        }
    }, [activePaymentData]);

    const onGetPaymentIntent = () => {
        setShowCardModal(true);
        dispatch(getPaymentIntentData());
    };

    const onRemovePayment = () => {
        setAlertOpen(false);
        dispatch(removePayment());
        dispatch(getSubscription("is_removed"));
    };

    const handleAlertOpen = () => {
        setAlertOpen(true);
    };

    const handleAlertClose = () => {
        setAlertOpen(false);
    };

    return (
        <Box sx={{ width: '100%', boxSizing: 'border-box', p: 2.5 }}>
            {(addPaymentData && addPaymentData.notification && addPaymentData.notification.show && addPaymentData.notification.messageContent != "") &&
                <Notification
                    is_open={addPaymentData.notification.show}
                    messageType={addPaymentData.notification.messageType}
                    messages={addPaymentData.notification.messageContent}
                    handleClose={() => dispatch(resetPaymentNotifications())}
                />
            }
            {(removePaymentData && removePaymentData.notification && removePaymentData.notification.show && removePaymentData.notification.messageContent != "") &&
                <Notification
                    is_open={removePaymentData.notification.show}
                    messageType={removePaymentData.notification.messageType}
                    messages={removePaymentData.notification.messageContent}
                    handleClose={() => dispatch(resetPaymentNotifications())}
                />
            }
            {(activePaymentData && activePaymentData.notification && activePaymentData.notification.show && activePaymentData.notification.messageContent != "") &&
                <Notification
                    is_open={activePaymentData.notification.show}
                    messageType={activePaymentData.notification.messageType}
                    messages={activePaymentData.notification.messageContent}
                    handleClose={() => dispatch(resetPaymentNotifications())}
                />
            }
            <Notification
                is_open={showError}
                messageType={showMessageType}
                messages={showMessage}
                handleClose={() => setShowError(false)}
            />
            
            {/* Core Framing Master Panel Surface */}
            <Paper 
                className="sc--cardhead--details"
                variant="none"
                sx={{ 
                    width: '100%',
                    backgroundColor: '#FFFFFF',
                    border: '1px solid #E2E8F0',
                    borderRadius: '16px',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.02)',
                    overflow: 'hidden'
                }}
            >
                <CardHeader 
                    title={
                        <Typography variant="h6" component="h1" sx={{ fontWeight: 800, color: '#0F172A', fontSize: '16px', m: 0 }}>
                            Payment Method
                        </Typography>
                    } 
                    action={
                        <Tooltip title="Back to Billing" placement="top">
                            <Link to="/settings/billing" style={{ textDecoration: 'none' }}>
                                <IconButton 
                                    sx={{ 
                                        color: '#64748B', 
                                        borderRadius: '8px', 
                                        border: '1px solid #E2E8F0',
                                        p: 1,
                                        transition: 'all 0.15s ease-in-out',
                                        '&:hover': { backgroundColor: '#F1F5F9', color: '#1E293B', borderColor: '#CBD5E1' }
                                    }}
                                >
                                    <ArrowBackIosNewIcon sx={{ fontSize: '15px' }} />
                                </IconButton>
                            </Link>
                        </Tooltip>
                    }
                    sx={{ p: 2.5, backgroundColor: '#FFFFFF' }}
                />
                
                {((activePaymentData && activePaymentData.loading === true) || loadingEnable || (paymentIntentData && paymentIntentData.loading) || (activation && activation.loading)) && (
                    <LinearProgress sx={{ height: '3px', backgroundColor: '#E2E8F0', '& .MuiLinearProgress-bar': { backgroundColor: '#1976d2' } }} />
                )}
                
                <Divider sx={{ borderColor: '#E2E8F0' }} />
                
                <CardContent sx={{ p: 4, boxSizing: 'border-box' }}>
                    <Stack spacing={4}>
                        
                        {/* Address Warning Intercept Notification */}
                        {(activePaymentData && activePaymentData.loading === false) && !(addressbilling && addressbilling.data && addressbilling.data.billing_name) && (
                            <Box sx={{ 
                                width: '100%', 
                                maxWidth: '600px', 
                                // Explicitly enforce auto alignment over the top of the Stack 0 margin constraint
                                marginLeft: 'auto !important', 
                                marginRight: 'auto !important' 
                            }}>
                                <Alert 
                                    severity="warning"
                                    variant="outlined"
                                    sx={{ borderRadius: '12px', fontWeight: 500, fontSize: '13px', alignItems: 'center' }}
                                >
                                    Please update your billing address credentials to enable secure payment profile storage.
                                </Alert>
                            </Box>
                        )}

                        {/* Interactive Payment Configuration Core Matrix */}
                        <Grid container justifyContent="center" spacing={2}>
                            <Grid item xs={12} sm={8} md={6} lg={5}>
                                <Stack spacing={3} alignItems="center">
                                    
                                    {/* Sub-tier View A: Existing Registered Token Tokenized Card Display */}
                                    {(activePaymentData && activePaymentData.data && activePaymentData.data.length != 0 && activePaymentData.data[0].type && activePaymentData.data[0].type == "card") && (
                                        <>
                                            <Box sx={{ 
                                                width: '100%', 
                                                maxWidth: '380px', 
                                                '& .rchk__card': { boxShadow: '0 10px 15px -3px rgba(0,0,0,0.08) !important' }
                                            }}>
                                                <Card
                                                    number={"************" + activePaymentData.data[0].card.last4}
                                                    name={activePaymentData.data[0].billing_details.name}
                                                    expiry={activePaymentData.data[0].card.exp_month + "/" + activePaymentData.data[0].card.exp_year}
                                                    preview={true}
                                                    issuer={activePaymentData.data[0].card.brand && activePaymentData.data[0].card.brand}
                                                />
                                            </Box>
                                            
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
                                                {/* <Button 
                                                    variant="outlined"
                                                    size="small"
                                                    color="error"
                                                    onClick={handleAlertOpen}
                                                    sx={{
                                                        textTransform: 'none',
                                                        borderRadius: '10px',
                                                        fontWeight: 700,
                                                        fontSize: '13px',
                                                        height: '36px',
                                                        px: 3,
                                                        borderColor: '#FEE2E2',
                                                        backgroundColor: '#FFFFFF',
                                                        color: '#EF4444',
                                                        '&:hover': { borderColor: '#FCA5A5', backgroundColor: '#FEF2F2' }
                                                    }}
                                                >
                                                    Remove Card
                                                </Button> */}
                                                
                                                <LoadingButton
                                                    variant="contained"
                                                    size="small"
                                                    disabled={
                                                        ((activePaymentData.data.paddle_status && activePaymentData.data.paddle_status == "deleted") ? true : false) ||
                                                        !(addressbilling && addressbilling.data && addressbilling.data.billing_name)
                                                    }
                                                    onClick={onGetPaymentIntent}
                                                    loading={
                                                        (addPaymentData && addPaymentData.loading) ||
                                                        (paymentIntentData && paymentIntentData.loading) ||
                                                        (loadingEnable) ||
                                                        (activePaymentData && activePaymentData.loading === true)
                                                    }
                                                    sx={{
                                                        textTransform: 'none',
                                                        borderRadius: '10px',
                                                        fontWeight: 700,
                                                        fontSize: '13px',
                                                        height: '36px',
                                                        px: 3,
                                                        boxShadow: 'none',
                                                        backgroundColor: '#1976d2',
                                                        color: '#FFFFFF',
                                                        '&:hover': { backgroundColor: '#115293', boxShadow: 'none' },
                                                        '&.Mui-disabled': { backgroundColor: '#E2E8F0', color: '#94A3B8' }
                                                    }}
                                                >
                                                    Update Method
                                                </LoadingButton>
                                            </Box>

                                            <Dialog
                                                open={alertOpen}
                                                onClose={handleAlertClose}
                                                aria-labelledby="alert-dialog-title"
                                                aria-describedby="alert-dialog-description"
                                                PaperProps={{
                                                    sx: { borderRadius: '14px', p: 1 }
                                                }}
                                            >
                                                <DialogTitle id="alert-dialog-title" sx={{ fontWeight: 700, color: '#0F172A', fontSize: '15px', pb: 1 }}>
                                                    {"Remove payment method?"}
                                                </DialogTitle>
                                                <DialogActions sx={{ gap: 0.5 }}>
                                                    <Button 
                                                        onClick={handleAlertClose}
                                                        sx={{ textTransform: 'none', color: '#475569', fontWeight: 600, borderRadius: '8px' }}
                                                    >
                                                        No, Keep
                                                    </Button>
                                                    <Button 
                                                        onClick={onRemovePayment} 
                                                        autoFocus
                                                        variant="contained"
                                                        color="error"
                                                        sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '8px', boxShadow: 'none', '&:hover': { boxShadow: 'none' } }}
                                                    >
                                                        Yes, Remove
                                                    </Button>
                                                </DialogActions>
                                            </Dialog>
                                        </>
                                    )}

                                    {/* Sub-tier View B: Fallback Empty Pipeline State Add Trigger Block */}
                                    {(activePaymentData && activePaymentData.data && activePaymentData.data.length == 0) && (
                                        <Box sx={{ width: '100%', py: 2 }}>
                                            <LoadingButton
                                                variant="outlined"
                                                fullWidth
                                                onClick={onGetPaymentIntent}
                                                disabled={!(addressbilling && addressbilling.data && addressbilling.data.billing_name)}
                                                loading={
                                                    (addPaymentData && addPaymentData.loading) ||
                                                    (paymentIntentData && paymentIntentData.loading) ||
                                                    (loadingEnable) ||
                                                    (activePaymentData && activePaymentData.loading === true)
                                                }
                                                sx={{
                                                    textTransform: 'none',
                                                    borderRadius: '12px',
                                                    fontWeight: 700,
                                                    fontSize: '14px',
                                                    py: 4,
                                                    borderStyle: 'dashed',
                                                    borderWidth: '1.5px',
                                                    borderColor: '#1976d2',
                                                    color: '#1976d2',
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    gap: 1.5,
                                                    '&:hover': { borderStyle: 'dashed', borderWidth: '1.5px', backgroundColor: 'rgba(25, 118, 210, 0.04)', borderColor: '#115293' },
                                                    '&.Mui-disabled': { borderColor: '#E2E8F0', color: '#94A3B8' }
                                                }}
                                            >
                                                <AddCardOutlinedIcon sx={{ fontSize: '32px' }} />
                                                Add Payment Method
                                            </LoadingButton>
                                        </Box>
                                    )}

                                </Stack>
                            </Grid>
                        </Grid>
                    </Stack>
                </CardContent>
            </Paper>

            {/* Global Stripe Form Execution Dialogue Window Context */}
            <Modal
                open={showCardModal && !!(paymentIntentData && paymentIntentData.data && paymentIntentData.data.client_secret)}
                onClose={() => setShowCardModal(false)}
                aria-labelledby="modal-modal-title"
                aria-describedby="modal-modal-description"
                closeAfterTransition
            >
                <Box sx={enterpriseModalContainerStyles}>
                    {paymentIntentData && paymentIntentData.data && paymentIntentData.data.client_secret && (
                        <Elements stripe={stripePromise}>
                            <CheckoutForm
                                client_secret={paymentIntentData.data.client_secret}
                                dispatch={dispatch}
                                setShowError={setShowError}
                                setShowMessageType={setShowMessageType}
                                setShowMessage={setShowMessage}
                                setloadingEnable={setloadingEnable}
                                loading={
                                    (addPaymentData && addPaymentData.loading) ||
                                    (paymentIntentData && paymentIntentData.loading) ||
                                    (loadingEnable) ||
                                    (activePaymentData && activePaymentData.loading === true)
                                }
                                addressbilling={addressbilling}
                            />
                        </Elements>
                    )}
                </Box>
            </Modal>
        </Box>
    );
};

export default PaymentPage;